import React, { useState } from 'react';
import QrModal from './QrModal';
import ScorecardCard from './ScorecardCard';
import NoListingNotice from './NoListingNotice';

// Values are translated here, at module load, so the __() call below sees a
// string literal - the translations:scan-project-and-add-to-db regex only
// matches a literal first argument, not a variable such as card.title.
const LINK_CARDS = [
    {
        key: 'rating',
        title: __('Rate the customer experience'),
        text: __('5-stars generate a review / 1-4 generate private feedback'),
        urlKey: 'rating_static',
        qrKey: 'rating_qr',
        buttonLabel: __('Go to Rating Page'),
        icon: 'fa-star',
        tone: 'gold',
    },
    {
        key: 'feedback',
        title: __('Private Feedback'),
        text: __('Collect private feedback from your customers.'),
        urlKey: 'feedback',
        qrKey: 'feedback_qr',
        buttonLabel: __('Go to Feedback Page'),
        icon: 'fa-shield-alt',
    },
    {
        key: 'testimonial',
        title: __('Testimonial Videos'),
        text: __('Have customers record a video testimonial and send to the business.'),
        urlKey: 'testimonial',
        qrKey: 'testimonial_qr',
        buttonLabel: __('Go to Video Testimonial Page'),
        // `far` (regular) is the outline cut of the icon - the play badge is
        // drawn as an outline, every other card icon is solid.
        icon: 'fa-play-circle',
        iconStyle: 'far',
    },
];

function LinkCardIcon({ icon, iconStyle = 'fas', tone = 'blue' }) {
    return (
        <div className={`demo-island-link-icon demo-island-link-icon--${tone} rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3`}>
            <i className={`${iconStyle} ${icon} fa-lg`} aria-hidden="true" />
        </div>
    );
}

function LinkAction({ onClick, href, label, icon }) {
    const className = 'btn btn-link btn-block d-flex align-items-center justify-content-between px-0';
    const content = (
        <>
            {label}
            <i className={`fas ${icon}`} aria-hidden="true" />
        </>
    );

    return href
        ? <a href={href} target="_blank" rel="noopener" className={className}>{content}</a>
        : <button type="button" className={className} onClick={onClick}>{content}</button>;
}

function LinkCard({ card, url, colClass, onToast, onViewQr }) {
    const handleCopy = () => {
        navigator.clipboard.writeText(url).then(
            () => onToast({ type: 'success', text: __('Link copied') }),
            () => onToast({ type: 'error', text: __('Could not copy the link') }),
        );
    };

    return (
        <div className={`${colClass} demo-edit-link-card`} data-testid={`demo-edit-link-${card.key}`}>
            <div className="card h-100 text-center">
                <div className="card-body d-flex flex-column">
                    <LinkCardIcon icon={card.icon} iconStyle={card.iconStyle} tone={card.tone} />
                    <h5 className="card-title">{card.title}</h5>
                    <p className="card-text text-muted">{card.text}</p>
                    <hr className="w-100" />
                    <div className="mt-auto text-left">
                        <LinkAction label={__('View QR code')} icon="fa-qrcode" onClick={() => onViewQr(card)} />
                        <LinkAction label={card.buttonLabel} icon="fa-external-link-alt" href={url} />
                        <LinkAction label={__('Copy Link')} icon="fa-clone" onClick={handleCopy} />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function DemoLinks({ demo, endpoints, polling, supportUrl, onScorecard, onToast }) {
    const [modalCard, setModalCard] = useState(null);

    const showTestimonial = demo.request_types.includes('testimonial') && !!demo.links.testimonial;
    const cards = LINK_CARDS.filter((card) => card.key !== 'testimonial' || showTestimonial);
    // A demo with a place id but no demo_scorecards row yet ('unavailable') would otherwise be
    // stuck on "Generating..." forever - hide the slot instead, but keep ScorecardCard mounted so
    // its useScorecardLive subscription survives and the card can flip live once one is requested.
    const scorecardHidden = !!demo.google_place_id && demo.scorecard.status === 'unavailable';
    const totalCards = cards.length + (scorecardHidden ? 0 : 1);
    const colClass = totalCards === 3 ? 'col-md-4 col-sm-6 mb-3' : 'col-md-3 col-sm-6 mb-3';

    return (
        <div data-testid="demo-edit-links">
            <h2 className="h5">{__('Demo Links')}</h2>
            <div className="row">
                {cards.map((card) => (
                    <LinkCard
                        key={card.key}
                        card={card}
                        url={demo.links[card.urlKey]}
                        colClass={colClass}
                        onToast={onToast}
                        onViewQr={setModalCard}
                    />
                ))}
                <div
                    className={`${colClass} demo-edit-link-card${scorecardHidden ? ' d-none' : ''}`}
                    data-testid="demo-edit-link-scorecard"
                >
                    <div className="card h-100 text-center">
                        <div className="card-body d-flex flex-column">
                            <LinkCardIcon icon="fa-chart-bar" tone="blue" />
                            <h5 className="card-title">{__('Reputation Scorecard')}</h5>
                            <p className="card-text text-muted">{__('Show your customer how they look online.')}</p>
                            <hr className="w-100" />
                            <div className="mt-auto text-left">
                                {demo.google_place_id ? (
                                    <ScorecardCard
                                        demo={demo}
                                        endpoints={endpoints}
                                        polling={polling}
                                        onScorecard={onScorecard}
                                        onToast={onToast}
                                    />
                                ) : (
                                    <NoListingNotice supportUrl={supportUrl} />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {modalCard && (
                <QrModal
                    title={modalCard.title}
                    qr={demo.links[modalCard.qrKey]}
                    onClose={() => setModalCard(null)}
                />
            )}
        </div>
    );
}
